"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  const menuOrder = ["The Axebox Edge", "Our Expertise", "Connect With Us", "Our Sectors", "Reserve Your Demo"];
  const sortedMenuItems = [...menuItems].sort((a, b) => {
    const indexA = menuOrder.findIndex(item => item.toLowerCase() === a.label.toLowerCase());
    const indexB = menuOrder.findIndex(item => item.toLowerCase() === b.label.toLowerCase());
    const sortA = indexA === -1 ? 999 : indexA;
    const sortB = indexB === -1 ? 999 : indexB;
    return sortA - sortB;
  });
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {sortedMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-mono text-sm text-muted-foreground hover:text-foreground transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-8 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  const menuOrder = ["The Axebox Edge", "Our Expertise", "Connect With Us", "Our Sectors", "Reserve Your Demo"];
  const sortedMenuItems = [...menuItems].sort((a, b) => {
    const indexA = menuOrder.findIndex(item => item.toLowerCase() === a.label.toLowerCase());
    const indexB = menuOrder.findIndex(item => item.toLowerCase() === b.label.toLowerCase());
    const sortA = indexA === -1 ? 999 : indexA;
    const sortB = indexB === -1 ? 999 : indexB;
    return sortA - sortB;
  });
  return <nav className="flex flex-col gap-6 mt-12 items-center">
      {sortedMenuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-mono text-2xl text-foreground hover:text-primary transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="mt-8 w-full max-w-xs" data-index="cta">
        <Button asChild variant="outline" className="w-full rounded-none border-destructive text-foreground hover:bg-destructive hover:text-destructive-foreground font-mono uppercase tracking-widest h-12" onClick={onItemClick}>
          <Link to="/ReserveYourDemoPage">Book a Demo</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="header" className={`dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-transparent"}`}>
        <div className="container mx-auto px-4 md:px-6 h-24 flex items-center justify-between">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <img src="https://axebox-media.com/wp-content/uploads/2026/03/image_pdoqeak0.png" alt="Axebox Media" className="logo object-contain max-w-full !w-[100px] !h-[99px]" onError={e => {
              e.currentTarget.src = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586938/img/axebox_logo_fallback.png";
              e.currentTarget.onerror = null;
            }} />
            </Link>
          </div>

          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="43">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          

          <div className="md:hidden flex items-center">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" className="text-foreground hover:bg-accent/50">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && <div className="dark bg-background text-foreground fixed inset-0 z-[100] flex flex-col">
          <div className="container mx-auto px-4 h-24 flex items-center justify-between border-b border-border">
            <div className="flex-shrink-0 flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586938/img/axebox_media_logo_v2.png" alt="Axebox Media" className="logo object-contain max-w-full" onError={e => {
            e.currentTarget.src = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586938/img/axebox_logo_fallback.png";
            e.currentTarget.onerror = null;
          }} />
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="text-foreground hover:bg-accent/50">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-8 flex flex-col items-center justify-center">
            <MenuProvider menu_id="43">
              <MobileNavConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </>;
}